import React, { useState, useEffect, useContext, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Headphones, Mic, BookOpen, PenTool, ChevronRight, CheckCircle,
  ArrowRight, Layers, Target, Crosshair, Award, BarChart3, Loader2
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import AuthContext from "@/contexts/AuthContext";
import SpeechRecorder from "@/components/SpeechRecorder";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_CONFIG = {
  1: { name: "Routing", icon: Layers, color: "#6366f1", bgColor: "bg-indigo-50", textColor: "text-indigo-700", borderColor: "border-indigo-300", desc: "Determining your starting level" },
  2: { name: "Targeted", icon: Target, color: "#0ea5e9", bgColor: "bg-sky-50", textColor: "text-sky-700", borderColor: "border-sky-300", desc: "Questions matched to your ability" },
  3: { name: "Precision", icon: Crosshair, color: "#f59e0b", bgColor: "bg-amber-50", textColor: "text-amber-700", borderColor: "border-amber-300", desc: "Pinpointing your exact proficiency level" },
};

const MODALITY_META = {
  listening: { icon: Headphones, label: "Listening", color: "bg-purple-100 text-purple-700" },
  speaking: { icon: Mic, label: "Speaking", color: "bg-blue-100 text-blue-700" },
  reading: { icon: BookOpen, label: "Reading", color: "bg-green-100 text-green-700" },
  writing: { icon: PenTool, label: "Writing", color: "bg-orange-100 text-orange-700" },
};

const LEVEL_NAMES = { 1: "Entering", 2: "Emerging", 3: "Developing", 4: "Expanding", 5: "Bridging", 6: "Reaching" };
const LEVEL_COLORS = {
  1: "bg-red-500", 2: "bg-orange-500", 3: "bg-yellow-500",
  4: "bg-blue-500", 5: "bg-indigo-500", 6: "bg-green-500",
};

const MSATExam = () => {
  const { gradeLevel } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  // MSAT state
  const [phase, setPhase] = useState("setup"); // setup | testing | transition | complete
  const [sessionId, setSessionId] = useState(null);
  const [currentStage, setCurrentStage] = useState(1);
  const [completedStage, setCompletedStage] = useState(0); // tracks last completed stage for transition
  const [nextStageData, setNextStageData] = useState(null); // holds next stage's questions
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [stageResults, setStageResults] = useState({});
  const [finalResult, setFinalResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedModality, setSelectedModality] = useState("listening");
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  // Elapsed time tracker (informational — WIDA ACCESS is untimed)
  useEffect(() => {
    if (phase !== 'testing') return;
    const interval = setInterval(() => setElapsedSeconds(prev => prev + 1), 1000);
    return () => clearInterval(interval);
  }, [phase]);

  const formatTime = (secs) => {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    return h > 0 ? `${h}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}` : `${m}:${String(s).padStart(2,'0')}`;
  };
  const [estimatedLevel, setEstimatedLevel] = useState(null);
  const [difficultyWindow, setDifficultyWindow] = useState(null);
  const [studentName, setStudentName] = useState(user?.name || "Student");

  const gradeLabels = {
    "K": "Kindergarten", "1": "Grade 1", "2": "Grade 2",
    "3-4": "Grades 3-4", "5-6": "Grades 5-6", "7-8": "Grades 7-8", "9-12": "Grades 9-12"
  };

  const currentQuestion = questions[currentIndex];
  const stageConfig = STAGE_CONFIG[currentStage];
  const modalityMeta = MODALITY_META[selectedModality];
  const ModalityIcon = modalityMeta?.icon || Headphones;

  // Start MSAT session
  const startMSAT = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/msat/start`, {
        grade_level: gradeLevel,
        modality: selectedModality,
        student_id: user?.user_id || "anonymous",
        student_name: studentName,
        form_id: "A",
      });
      setSessionId(res.data.session_id);
      setQuestions(res.data.questions);
      setCurrentStage(1);
      setCurrentIndex(0);
      setResponses({});
      setPhase("testing");
      toast.success(`Stage 1: ${res.data.question_count} routing questions loaded`);
    } catch (err) {
      toast.error("Failed to start MSAT session");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Submit current stage
  const submitStage = async () => {
    // Verify all questions answered
    const unanswered = questions.filter(q => !responses[q.id]);
    if (unanswered.length > 0) {
      toast.error(`Please answer all ${unanswered.length} remaining question${unanswered.length > 1 ? 's' : ''}`);
      const firstUnanswered = questions.findIndex(q => !responses[q.id]);
      setCurrentIndex(firstUnanswered);
      return;
    }

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/msat/${sessionId}/submit-stage`, {
        answers: responses,
      });

      if (res.data.stage === "complete") {
        setFinalResult(res.data.final_result);
        setPhase("complete");
        toast.success("Assessment complete!");
      } else {
        // Store stage result and transition
        const prevResult = res.data.previous_stage_result;
        setStageResults(prev => ({
          ...prev,
          [currentStage]: prevResult,
        }));
        setEstimatedLevel(res.data.estimated_level);
        setDifficultyWindow(res.data.difficulty_window);
        setCompletedStage(currentStage);
        setNextStageData({
          stage: res.data.stage,
          questions: res.data.questions,
        });

        // Show transition screen (don't update currentStage yet)
        setPhase("transition");
      }
    } catch (err) {
      toast.error("Failed to submit stage");
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAnswer = useCallback((questionId, answer) => {
    setResponses(prev => ({ ...prev, [questionId]: answer }));
  }, []);

  const goNext = () => {
    if (currentIndex < questions.length - 1) setCurrentIndex(currentIndex + 1);
  };
  const goPrev = () => {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  };

  const answeredCount = questions.filter(q => responses[q.id]).length;
  const progressPct = questions.length > 0 ? (answeredCount / questions.length) * 100 : 0;

  // ─── SETUP PHASE ───
  if (phase === "setup") {
    return (
      <div className="max-w-3xl mx-auto px-6 py-12" data-testid="msat-setup">
        <div className="border-2 border-black p-8 mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-[#002FA7] flex items-center justify-center">
              <Layers size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Multi-Stage Adaptive Test
              </h1>
              <p className="text-sm text-gray-500">{gradeLabels[gradeLevel] || gradeLevel}</p>
            </div>
          </div>
          <p className="text-gray-600 mt-3">
            This assessment adapts to your ability in real time.
            It has 3 stages that get easier or harder based on how you do.
          </p>
        </div>

        {/* How It Works */}
        <div className="border-2 border-gray-200 mb-8" data-testid="msat-how-it-works">
          <div className="p-5 border-b border-gray-200">
            <h2 className="font-bold text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>How It Works</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3">
            {[1, 2, 3].map(stage => {
              const cfg = STAGE_CONFIG[stage];
              const Icon = cfg.icon;
              return (
                <div key={stage} className={`p-5 ${stage < 3 ? 'md:border-r' : ''} border-b md:border-b-0 border-gray-200`}>
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center`} style={{ backgroundColor: cfg.color + '22', color: cfg.color }}>
                      <Icon size={16} />
                    </div>
                    <span className="font-bold text-sm" style={{ color: cfg.color }}>Stage {stage}</span>
                  </div>
                  <div className="font-semibold mb-1">{cfg.name}</div>
                  <p className="text-sm text-gray-500">{cfg.desc}</p>
                  <div className="text-xs text-gray-400 mt-2">
                    {stage === 1 ? "~6 questions" : stage === 2 ? "~8 questions" : "~6 questions"}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Select Modality */}
        <div className="border-2 border-gray-200 mb-8" data-testid="msat-modality-select">
          <div className="p-5 border-b border-gray-200">
            <h2 className="font-bold text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Select Modality</h2>
            <p className="text-sm text-gray-500">Choose which language skill to assess</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4">
            {Object.entries(MODALITY_META).map(([key, meta]) => {
              const Icon = meta.icon;
              const isSelected = selectedModality === key;
              return (
                <button
                  key={key}
                  onClick={() => setSelectedModality(key)}
                  className={`p-5 text-left transition-all border-r last:border-r-0 border-gray-200 ${isSelected ? 'bg-[#002FA7]/5 border-b-4 border-b-[#002FA7]' : 'hover:bg-gray-50'}`}
                  data-testid={`msat-modality-${key}`}
                >
                  <div className={`w-10 h-10 ${meta.color} flex items-center justify-center mb-2`}>
                    <Icon size={20} />
                  </div>
                  <div className="font-semibold">{meta.label}</div>
                  {isSelected && <div className="text-xs text-[#002FA7] mt-1 font-medium">Selected</div>}
                </button>
              );
            })}
          </div>
        </div>

        {/* Start Button */}
        <Button
          onClick={startMSAT}
          disabled={loading}
          className="w-full h-14 bg-[#002FA7] hover:bg-[#002FA7]/90 text-white text-lg font-bold"
          data-testid="msat-start-btn"
        >
          {loading ? (
            <><Loader2 size={20} className="animate-spin mr-2" /> Starting...</>
          ) : (
            <>Begin Multi-Stage Test <ArrowRight size={20} className="ml-2" /></>
          )}
        </Button>

        <p className="text-center text-sm text-gray-400 mt-4">
          Estimated time: ~15–20 minutes for all 3 stages
        </p>
      </div>
    );
  }

  // ─── TRANSITION PHASE ───
  if (phase === "transition") {
    const prevStageResult = stageResults[completedStage];
    const completedConfig = STAGE_CONFIG[completedStage];
    const nextStage = completedStage + 1;
    const nextConfig = STAGE_CONFIG[nextStage];
    const CompletedIcon = completedConfig?.icon || Layers;
    const NextIcon = nextConfig?.icon || Target;

    const handleContinue = () => {
      if (nextStageData) {
        setQuestions(nextStageData.questions);
        setCurrentStage(nextStageData.stage);
        setCurrentIndex(0);
        setResponses({});
      }
      setPhase("testing");
    };

    return (
      <div className="max-w-2xl mx-auto px-6 py-12" data-testid="msat-transition">
        <div className="text-center mb-10">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: (completedConfig?.color || '#6366f1') + '22', color: completedConfig?.color || '#6366f1' }}>
            <CheckCircle size={32} />
          </div>
          <h1 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Stage {completedStage} Complete
          </h1>
          <p className="text-gray-500">{completedConfig?.name || 'Stage'} finished</p>
        </div>

        {/* Stage Result Card */}
        <div className={`border-2 ${completedConfig?.borderColor || 'border-gray-300'} p-6 mb-6`} data-testid="msat-stage-result">
          <div className="flex items-center justify-between mb-4">
            <span className="font-semibold text-gray-600">Your Performance</span>
            <span className={`text-2xl font-bold ${completedConfig?.textColor || 'text-gray-700'}`}>
              {prevStageResult?.correct}/{prevStageResult?.total} correct
            </span>
          </div>
          <div className="w-full bg-gray-100 h-3 mb-3">
            <div
              className="h-full transition-all duration-700"
              style={{ width: `${prevStageResult?.percentage || 0}%`, backgroundColor: completedConfig?.color || '#6366f1' }}
            />
          </div>
          <div className="flex justify-between text-sm text-gray-500">
            <span>{Math.round(prevStageResult?.percentage || 0)}%</span>
            <span>Estimated Level: L{estimatedLevel} ({LEVEL_NAMES[estimatedLevel] || ""})</span>
          </div>
        </div>

        {/* What's Next */}
        {nextConfig && (
          <div className="border-2 border-gray-200 p-6 mb-8" data-testid="msat-next-stage-info">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: nextConfig.color + '22', color: nextConfig.color }}>
                <NextIcon size={20} />
              </div>
              <div>
                <div className="font-bold">Up Next: Stage {nextStage} — {nextConfig.name}</div>
                <div className="text-sm text-gray-500">{nextConfig.desc}</div>
              </div>
            </div>
            {difficultyWindow && (
              <div className="flex items-center gap-2 mt-3">
                <span className="text-sm text-gray-500">Difficulty window:</span>
                {difficultyWindow.map(l => (
                  <span key={l} className={`px-2 py-0.5 text-xs font-bold text-white rounded ${LEVEL_COLORS[l]}`}>
                    L{l}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        <Button
          onClick={handleContinue}
          className="w-full h-14 text-lg font-bold text-white"
          style={{ backgroundColor: nextConfig?.color || '#002FA7' }}
          data-testid="msat-continue-btn"
        >
          Continue to Stage {nextStage} <ArrowRight size={20} className="ml-2" />
        </Button>
      </div>
    );
  }

  // ─── COMPLETE PHASE ───
  if (phase === "complete" && finalResult) {
    const stageBreakdown = finalResult.stage_breakdown || {};
    return (
      <div className="max-w-3xl mx-auto px-6 py-12" data-testid="msat-results">
        {/* Hero Result */}
        <div className="text-center mb-10">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
            <Award size={40} className="text-green-600" />
          </div>
          <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Assessment Complete
          </h1>
          <p className="text-gray-500">{gradeLabels[gradeLevel]} — {modalityMeta?.label}</p>
        </div>

        {/* Overall Score */}
        <div className="border-2 border-black p-8 mb-8 text-center" data-testid="msat-final-score">
          <div className="text-sm uppercase tracking-widest text-gray-500 mb-2">Your Proficiency Level</div>
          <div className="flex items-center justify-center gap-4 mb-3">
            <div className={`w-16 h-16 rounded-full ${LEVEL_COLORS[finalResult.wida_level]} flex items-center justify-center`}>
              <span className="text-3xl font-black text-white">L{finalResult.wida_level}</span>
            </div>
            <div className="text-left">
              <div className="text-2xl font-bold">{finalResult.level_name}</div>
              <div className="text-gray-500">Proficiency Score: {finalResult.wida_score}</div>
            </div>
          </div>
          <div className="text-sm text-gray-500 mt-2">
            {finalResult.total_correct}/{finalResult.total_questions} correct ({finalResult.percentage}%)
          </div>
        </div>

        {/* Stage Breakdown */}
        <div className="border-2 border-gray-200 mb-8" data-testid="msat-stage-breakdown">
          <div className="p-5 border-b border-gray-200">
            <h2 className="font-bold text-lg flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <BarChart3 size={20} /> Stage-by-Stage Breakdown
            </h2>
          </div>
          <div className="divide-y divide-gray-200">
            {Object.entries(stageBreakdown).map(([stageNum, result]) => {
              const cfg = STAGE_CONFIG[parseInt(stageNum)];
              if (!cfg) return null;
              const Icon = cfg.icon;
              return (
                <div key={stageNum} className="p-5 flex items-center gap-4" data-testid={`msat-stage-${stageNum}-result`}>
                  <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: cfg.color + '22', color: cfg.color }}>
                    <Icon size={18} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold">Stage {stageNum}: {cfg.name}</span>
                      <span className={`px-2 py-0.5 text-xs font-bold text-white rounded ${LEVEL_COLORS[result.estimated_level]}`}>
                        L{result.estimated_level}
                      </span>
                    </div>
                    <div className="w-full bg-gray-100 h-2">
                      <div className="h-full rounded-full" style={{ width: `${result.percentage}%`, backgroundColor: cfg.color }} />
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="font-bold">{result.correct}/{result.total}</div>
                    <div className="text-xs text-gray-500">{Math.round(result.percentage)}%</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-4">
          <Button
            onClick={() => { setPhase("setup"); setFinalResult(null); setStageResults({}); }}
            variant="outline"
            className="flex-1 h-12"
            data-testid="msat-retake-btn"
          >
            Take Again
          </Button>
          <Button
            onClick={() => navigate(`/exam/setup/${gradeLevel}`)}
            className="flex-1 h-12 bg-[#002FA7] hover:bg-[#002FA7]/90 text-white"
            data-testid="msat-back-setup-btn"
          >
            Back to Exam Setup
          </Button>
        </div>
      </div>
    );
  }

  // ─── TESTING PHASE ───
  if (!currentQuestion) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="animate-spin text-[#002FA7]" size={32} />
      </div>
    );
  }

  const isMultipleChoice = currentQuestion.question_type === "multiple_choice";
  const isSpeaking = currentQuestion.question_type === "speaking_prompt" || currentQuestion.modality === "speaking";
  const isWriting = currentQuestion.question_type === "constructed_response" || currentQuestion.modality === "writing";
  const isLastQuestion = currentIndex === questions.length - 1;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6" data-testid="msat-testing">
      {/* Stage Header */}
      <div className={`border-2 ${stageConfig.borderColor} ${stageConfig.bgColor} p-4 mb-6`} data-testid="msat-stage-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ backgroundColor: stageConfig.color, color: 'white' }}>
              {React.createElement(stageConfig.icon, { size: 18 })}
            </div>
            <div>
              <div className="font-bold" style={{ color: stageConfig.color }}>
                Stage {currentStage}: {stageConfig.name}
              </div>
              <div className="text-xs text-gray-500">{stageConfig.desc}</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className={`flex items-center gap-1.5 px-3 py-1 ${modalityMeta.color}`}>
              <ModalityIcon size={14} />
              <span className="text-xs font-semibold">{modalityMeta.label}</span>
            </div>
            {estimatedLevel && (
              <div className="text-xs text-gray-500">
                Est. L{estimatedLevel}
              </div>
            )}
          </div>
        </div>

        {/* Stage progress */}
        <div className="flex items-center gap-2 mt-3">
          {[1, 2, 3].map(s => (
            <div key={s} className="flex-1">
              <div className={`h-1.5 rounded-full ${s < currentStage ? 'bg-green-500' : s === currentStage ? '' : 'bg-gray-200'}`}
                style={s === currentStage ? { backgroundColor: stageConfig.color } : {}} />
            </div>
          ))}
        </div>
      </div>

      {/* Question Progress */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm text-gray-500">
          Question {currentIndex + 1} of {questions.length}
        </span>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-400 font-mono" data-testid="msat-elapsed-time" title="Elapsed time (WIDA ACCESS is untimed)">{formatTime(elapsedSeconds)}</span>
          <span className="text-sm text-gray-500">
            {answeredCount}/{questions.length} answered
          </span>
        </div>
      </div>
      <Progress value={progressPct} className="h-2 mb-6" />

      {/* Question Card */}
      <div className="border-2 border-gray-200 mb-6" data-testid="msat-question-card">
        {/* Teacher Reads Aloud Banner */}
        {currentQuestion.teacher_reads && (
          <div className="mx-5 mt-4 bg-amber-50 border-2 border-amber-300 p-4 flex items-start gap-3" data-testid="msat-teacher-reads">
            <div className="w-8 h-8 bg-amber-500 flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-sm">T</span>
            </div>
            <div>
              <div className="font-bold text-amber-800 text-sm">Teacher: Read Aloud</div>
              <p className="text-xs text-amber-700 mt-0.5">This question is designed to be read aloud by the teacher. Please read the passage and question to the student.</p>
            </div>
          </div>
        )}

        {/* Big Picture Image (bp_image_url) */}
        {currentQuestion.bp_image_url && (
          <div className="px-5 pt-3">
            <div className="border-2 border-blue-200 overflow-hidden" data-testid="msat-bp-image">
              <img src={currentQuestion.bp_image_url} alt="Scene" className="w-full h-48 sm:h-64 object-contain bg-gray-50" />
            </div>
          </div>
        )}

        {/* Graphic Support Image (graphic_support_url) — for questions without bp_image */}
        {currentQuestion.graphic_support_url && !currentQuestion.bp_image_url && (
          <div className="px-5 pt-3">
            <div className="border-2 border-blue-200 overflow-hidden" data-testid="msat-graphic-image">
              <img src={currentQuestion.graphic_support_url} alt="Picture prompt" className="w-full h-40 sm:h-56 object-contain bg-gray-50" />
              <div className="bg-blue-50 px-4 py-2 text-xs font-bold text-blue-700 uppercase tracking-wide">
                {currentQuestion.modality === 'writing' ? 'Look at the picture above. Use it to help you write.'
                  : currentQuestion.modality === 'speaking' ? 'Look at the picture above. Use it to help you respond.'
                  : 'Look at the picture above.'}
              </div>
            </div>
          </div>
        )}

        {/* Per-question support image (shown alongside Big Picture for added context) */}
        {currentQuestion.graphic_support_url && currentQuestion.bp_image_url && (
          <div className="px-5 pt-3 flex items-center gap-3" data-testid="msat-question-support-image">
            <div className="border-2 border-blue-200 rounded-lg overflow-hidden flex-shrink-0 w-28 h-28">
              <img src={currentQuestion.graphic_support_url} alt="Question support" className="w-full h-full object-contain bg-gray-50" />
            </div>
            <div className="text-xs text-blue-600 font-medium italic">Look at this picture to help answer the question.</div>
          </div>
        )}

        {/* WIDA level badge */}
        {currentQuestion.wida_level && (
          <div className="px-5 pt-4">
            <span className={`inline-block px-2 py-0.5 text-xs font-bold text-white rounded ${LEVEL_COLORS[currentQuestion.wida_level]}`}>
              Proficiency Level {currentQuestion.wida_level}
            </span>
          </div>
        )}

        {/* Stimulus */}
        {currentQuestion.stimulus && (
          <div className="px-5 pt-3">
            <div className="bg-gray-50 border border-gray-200 p-4 text-sm text-gray-700 whitespace-pre-line" data-testid="msat-stimulus">
              {currentQuestion.stimulus}
            </div>
          </div>
        )}

        {/* Question text */}
        <div className="p-5">
          <h3 className="text-lg font-semibold mb-5" data-testid="msat-question-text">
            {currentQuestion.question_text}
          </h3>

          {/* Multiple Choice */}
          {isMultipleChoice && currentQuestion.options && (
            <RadioGroup
              value={responses[currentQuestion.id] || ""}
              onValueChange={(val) => handleAnswer(currentQuestion.id, val)}
              className="space-y-3"
              data-testid="msat-answer-options"
            >
              {currentQuestion.options.map((option, i) => (
                <Label
                  key={i}
                  className={`flex items-center gap-3 p-4 border-2 cursor-pointer transition-all ${
                    responses[currentQuestion.id] === option
                      ? 'border-[#002FA7] bg-[#002FA7]/5'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid={`msat-option-${i}`}
                >
                  <RadioGroupItem value={option} />
                  <span className="font-medium text-sm text-gray-600 mr-2">{String.fromCharCode(65 + i)}.</span>
                  <span>{option}</span>
                </Label>
              ))}
            </RadioGroup>
          )}

          {/* Speaking */}
          {isSpeaking && !isMultipleChoice && (
            <div className="space-y-4">
              <SpeechRecorder
                onTranscription={(text) => { if (text) handleAnswer(currentQuestion.id, text); }}
                questionContext="Record your spoken response"
              />
              <div className="text-xs text-gray-400">Or type your response:</div>
              <Textarea
                placeholder="Type your response here..."
                value={responses[currentQuestion.id] || ""}
                onChange={(e) => handleAnswer(currentQuestion.id, e.target.value)}
                className="min-h-[100px] border-2 border-gray-300 focus:border-[#002FA7] text-base p-4"
                data-testid="msat-text-input"
              />
            </div>
          )}

          {/* Writing */}
          {isWriting && !isMultipleChoice && !isSpeaking && (
            <div>
              <Textarea
                placeholder="Write your response here..."
                value={responses[currentQuestion.id] || ""}
                onChange={(e) => handleAnswer(currentQuestion.id, e.target.value)}
                className="min-h-[200px] border-2 border-gray-300 focus:border-[#002FA7] text-base p-4"
                data-testid="msat-writing-input"
              />
              {currentQuestion.scaffold_q2 && (
                <div className="mt-3 bg-amber-50 border border-amber-200 p-3">
                  <div className="text-xs font-bold text-amber-700 mb-1">NEED HELP?</div>
                  <ul className="text-sm text-amber-800 space-y-1">
                    <li>{currentQuestion.scaffold_q2}</li>
                    {currentQuestion.scaffold_q3 && <li>{currentQuestion.scaffold_q3}</li>}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between" data-testid="msat-navigation">
        <Button
          onClick={goPrev}
          disabled={currentIndex === 0}
          variant="outline"
          className="h-11"
          data-testid="msat-prev-btn"
        >
          Previous
        </Button>

        {/* Question dots */}
        <div className="flex gap-1.5 overflow-x-auto max-w-[300px]">
          {questions.map((q, i) => (
            <button
              key={q.id}
              onClick={() => setCurrentIndex(i)}
              className={`w-7 h-7 flex-shrink-0 text-xs font-bold transition-all ${
                i === currentIndex
                  ? 'bg-[#002FA7] text-white'
                  : responses[q.id]
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-200 text-gray-500 hover:bg-gray-300'
              }`}
              data-testid={`msat-dot-${i}`}
            >
              {i + 1}
            </button>
          ))}
        </div>

        {isLastQuestion ? (
          <Button
            onClick={submitStage}
            disabled={submitting}
            className="h-11 text-white font-bold"
            style={{ backgroundColor: stageConfig.color }}
            data-testid="msat-submit-stage-btn"
          >
            {submitting ? (
              <><Loader2 size={16} className="animate-spin mr-2" /> Submitting...</>
            ) : (
              <>Submit Stage {currentStage} <ChevronRight size={16} className="ml-1" /></>
            )}
          </Button>
        ) : (
          <Button
            onClick={goNext}
            className="h-11 bg-[#002FA7] hover:bg-[#002FA7]/90 text-white"
            data-testid="msat-next-btn"
          >
            Next <ChevronRight size={16} className="ml-1" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default MSATExam;
